<script lang="ts">
							import SectionComponent from '$lib/section/SectionComponent.svelte';
							import copy from "copy-to-clipboard"; 
							import { onMount } from "svelte";
							import PrevNextSection from '$lib/prevNext/PrevNextSection.svelte';

							interface ComponentProps {
								setVisible: (id: string, v: boolean) => void;
							}
							let { setVisible }: ComponentProps = $props();
							
							const visibleById = $state<Record<string, boolean>>({});

							/**
               * This function will go through all the 'pre' elements
               * on the page and add a copy button to them.
               * Thanks to: https://slavbasharov.com/blog/adding-click-to-copy-code-markdown-blog
               */
              onMount(() => {
                  const codeBlocks = document.querySelectorAll("pre");
                  codeBlocks.forEach((block) => {
                      const copyPrompt = document.createElement("div");
                      copyPrompt.className = "copy-prompt";
                      const copyPromptText = document.createElement("p");
                      copyPromptText.innerHTML = "👆 Click to copy";
                      copyPromptText.className = 'copy-prompt-p';
                      const copyIcon = document.createElement("img");
                      copyIcon.src = "/icons/copy-icon.svg";
                      copyIcon.className = "copy-prompt-img";
                      copyPrompt.appendChild(copyIcon);
                      copyPrompt.appendChild(copyPromptText);
                      block.appendChild(copyPrompt);
											block.querySelector('.copy-prompt > p')?.addEventListener('click', () => {
												if (block.querySelector('code')?.textContent !== null && block.querySelector('code')?.textContent !== undefined) {
													copy(block.querySelector('code')!.textContent!);
												}
												if (block.querySelector('.copy-prompt > p')?.innerHTML !== null && block.querySelector('.copy-prompt > p')?.innerHTML !== undefined) {
													block.querySelector('.copy-prompt > p')!.innerHTML = 'Copied!';
													setTimeout(() => {
														block.querySelector('.copy-prompt > p')!.innerHTML = '👆 Click to copy';
													}, 1000);
												}
											});
                  });
              });
		let prevLink= '/Examples/Website_Integration/Getting_Language_Information';
    let nextLink= '/Examples/Website_Integration/Adding_the_Editor';
    
    import Note from "$lib/notes/Note.svelte";
    import Figure from "$lib/figures/Figure.svelte";
</script>

<PrevNextSection {prevLink} {nextLink} />



<SectionComponent tag="h1" 
  id="including-model-information-1"
  bind:intersecting={visibleById["including-model-information-1"]}
  onIntersectChange={(visible) => setVisible("including-model-information-1", visible)}
>Including Model Information</SectionComponent>
<p>Now we have dealt with information from the DSL, we are ready to take
on the next challenge, to get information from the server.
Of course, the server needs to be running, so do not forget to execute
the following command in the folder <code>/packages/dsl</code>.</p>
<pre class="language-bash"><code class="language-bash">npm run server</code></pre>
<SectionComponent tag="h2" 
  id="opening-a-model-2"
  bind:intersecting={visibleById["opening-a-model-2"]}
  onIntersectChange={(visible) => setVisible("opening-a-model-2", visible)}
>Opening a Model</SectionComponent>
<p>To make things a bit more interesting we have implemented two ways to open a model.
First, you can add the name of the model to the url in the address bar of your browser.
Second, you can choose a model from the list of models available on the server. For this
option we need to open a dialog.
(Again, we are mimicking what the standard web app is capable of.)</p>
<p>Below is the code that implements this. Note that we have moved all code to actually
open the model, and to get the names of the available models, to the before mentioned class
<code>WebappConfigurator</code>.</p>
<pre class="language-ts"><code class="language-ts">// IntegrationExample/webapp/src/lib/main-app/WebappLayout.svelte#L24-L42

nMount(async () =&gt; &#123;
// If a model is given as parameter, open this model
// A new model is created when this model does not exist
const urlParams = new URLSearchParams(window.location.search);
const model = urlParams.get('model');
if (model !== null) &#123;
	openModel(model);
&#125; else &#123;
	// No model given as parameter, open the dialog to ask for it
	// Get list of models from server
	const names = await WebappConfigurator.getInstance().getAllModelNames();
	if (!!names &amp;&amp; names.length &gt; 0) &#123;
		// Make the names available for the dialog
		serverInfo.allModelNames = names;
	&#125;

	// open the app with the open/new model dialog
	dialogs.openModelDialogVisible = true;
&#125;</code></pre>
<p>So, let’s have a look at the <code>WebappConfigurator</code>. We first examine the <code>openModel</code> function. As you see,
the InMemoryModel class (here placed in the variable <code>modelStore</code>) from the <code>@freon4dsl/core</code> package
is doing the actual work. Here we take the model unit identifiers (a list of name-id pairs), find the first
unit in the list, and show it in the editor.</p>
<p>Two things need to be done for this to function properly.</p>
<ul>
<li>We need to clear all box caches, because they are no longer valid (done by <code>BoxFactory.clearCaches()</code>).</li>
<li>The projections also need to be cleared (done by <code>this.editorEnvironment?.projectionHandler.clear()</code>).</li>
</ul>
<p>Then we are all set to show the model in the editor, which will be explained
in <a
  href="/Examples/Website_Integration/Adding_the_Editor"
>Adding the Editor</a>.
Finally, we store information on the units in <code>updateUnitList()</code>.</p>
<p>More on Freon’s Box Model can be found in <a
  href="/Documentation/Under_the_Hood/Editor_Framework"
>Editor Framework</a></p>
<pre class="language-ts"><code class="language-ts">// IntegrationExample/webapp/src/lib/language/WebappConfigurator.ts#L99-L122

async openModel(modelName: string) &#123;
    if (!!this.modelStore) &#123;
        // create new model instance in memory and set its name
        await this.modelStore.openModel(modelName);
        const unitIdentifiers = this.modelStore.getUnitIdentifiers();
        LOGGER.log('unit identifiers: ' + JSON.stringify(unitIdentifiers));
        if (!!unitIdentifiers &amp;&amp; unitIdentifiers.length &gt; 0) &#123;
            // load the first unit and show it
            let first: boolean = true;
            for (const unitIdentifier of unitIdentifiers) &#123;
                if (first) &#123;
                    const unit = this.modelStore.getUnitByName(unitIdentifier.name);
                    LOGGER.log("UnitId " + unitIdentifier.name + " unit is " + unit?.name);
                    this.currentUnit = unit;
                    BoxFactory.clearCaches()
                    this.editorEnvironment?.projectionHandler.clear()
                    this.showUnit(this.currentUnit);
                    first = false;
                &#125;
            &#125;
        &#125;
        this.updateUnitList()
    &#125;
&#125;</code></pre>
<p>Upon submit, the Svelte dialog component also uses the <code>WebappConfigurator.openModel</code>
and <code>WebappConfigurator.newModel</code> functions to actually open or create the model. The if-statements
in the code check whether the user has entered a valid model name.</p>
<pre class="language-ts"><code class="language-ts">// IntegrationExample/webapp/src/lib/dialogs/OpenModelDialog.svelte#L47-L57

async function handleSubmit() &#123;
	const comm = WebappConfigurator.getInstance();
	// console.log('Handle "submit": ' + newName)
	if (internalSelected?.length &gt; 0) &#123; // should be checked first, because newName depends on it
		await comm.openModel(internalSelected);
		// $initializing = false;
	&#125; else if (!newNameInvalid() &amp;&amp; newName.length &gt; 0) &#123;
		console.log("CREATING NEW MODEL: " + newName);
		await comm.newModel(newName);
		// $initializing = false;
	&#125; else &#123;</code></pre>
<p>The result of our work is that upon opening the web page (without the model parameter in the address bar),
the open model dialog pops up.</p>
<Figure
imageName={'examples/WebsiteIntegration/info-from-server.png'}
caption={'The open model dialog'}
figureNumber={1}
/>
<SectionComponent tag="h2" 
  id="model-drawer-content-3"
  bind:intersecting={visibleById["model-drawer-content-3"]}
  onIntersectChange={(visible) => setVisible("model-drawer-content-3", visible)}
>Model Drawer Content</SectionComponent>
<p>As promised, we are going to show you how to add information to the Model Drawer. Here we are going to
focus on the inner list. All units in the model should be sorted based on their type, and every one of
them should have a dropdown menu associated that enables opening, saving etc. of that specific model unit.</p>
<p>As you can see, we loop over a list called <code>myUnits</code>, and check whether the list item has the same
type as <code>unitType</code>. This sorts out the units based on their type. Then we show the unit’s name, and add
a dropdown menu where every function takes the index in <code>myUnits</code> as parameter.</p>
<pre class="language-ts"><code class="language-ts">// IntegrationExample/webapp/src/lib/main-app/ModelInfo.svelte#L66-L90

&lt;Listgroup&gt;
    &#123;#each langInfo.unitTypes as unitType&#125;
        &lt;Heading tag="h5" class="pl-2"&gt;&#123;unitType&#125;&lt;/Heading&gt;
        &lt;ListgroupItem class="gap-2 text-base font-semibold"&gt;
            &lt;Listgroup&gt;
                &#123;#each myUnits as unit, index&#125;
                    &#123;#if unit.freLanguageConcept() === unitType&#125;
                        &lt;div class="flex justify-between"&gt;
                            &#123;unit.name&#125;
                            &lt;DotsHorizontalOutline class="dots-menu1 inline dark:text-white"/&gt;
                        &lt;/div&gt;
                        &lt;Dropdown triggeredBy=".dots-menu1"&gt;
                            &lt;DropdownItem onclick=&#123;() =&gt; (openUnit(index))&#125;&gt;Open&lt;/DropdownItem&gt;
                            &lt;DropdownItem onclick=&#123;() =&gt; (saveUnit(index))&#125;&gt;Save&lt;/DropdownItem&gt;
                            &lt;DropdownItem onclick=&#123;() =&gt; (renameUnit(index))&#125;&gt;Rename&lt;/DropdownItem&gt;
                            &lt;DropdownItem onclick=&#123;() =&gt; (deleteUnit(index))&#125;&gt;Delete&lt;/DropdownItem&gt;
                            &lt;DropdownItem slot="footer" onclick=&#123;() =&gt; (exportUnit(index))&#125;&gt;Export&lt;/DropdownItem&gt;
                        &lt;/Dropdown&gt;
                    &#123;/if&#125;
                &#123;/each&#125;
            &lt;/Listgroup&gt;
        &lt;/ListgroupItem&gt;
    &#123;/each&#125;
    &lt;!-- Instead of DotsHorizontalOutline we could use ChevronDownOutline--&gt;
&lt;/Listgroup&gt;</code></pre>
<p>To keep <code>myUnits</code> in sync with the current model, we use a Svelte effect. Here we use a state variable,
called <code>modelInfo</code>, that was set by the <code>WebappConfigurator.updateUnitList()</code>.</p>
<pre class="language-ts"><code class="language-ts">// IntegrationExample/webapp/src/lib/main-app/ModelInfo.svelte#L16-L28

$effect(() =&gt; &#123;
    myUnits = !!modelInfo.units &amp;&amp; modelInfo.units.length &gt; 0
        ? modelInfo.units.sort((u1: FreModelUnit, u2: FreModelUnit) =&gt; &#123;
            if (u1.name &gt; u2.name) &#123;
                return 1;
            &#125;
            if (u1.name &lt; u2.name) &#123;
                return -1;
            &#125;
            return 0;
        &#125;)
        : [];
&#125;);</code></pre>
<p>When we open the model information drawer, we can see the result of all our work.</p>
<Figure
imageName={'examples/WebsiteIntegration/model-drawer.png'}
caption={'Open drawer showing the model information'}
figureNumber={2}
/>


<PrevNextSection {prevLink} {nextLink} />